import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import { RichText } from '../lib/rich'
import { useStreamRunner } from '../lib/useStream'
import type { DialogueImage, Illustration, Me, Rewards, Task } from '../lib/types'

// Дольше этого ответ на реплику ждать бессмысленно: разбор на сервере доводится
// до конца и без браузера, а модель отвечает за минуту-две даже в худшем случае.
// Старше — значит, сайт перезапустили посреди разбора, и реплику надо повторить.
const ОЖИДАНИЕ_ОТВЕТА_С = 300
// Через столько секунд без единого слова честно говорим, что ответ задерживается.
const ДОЛГО_С = 30

type Reply = {
  role: 'user' | 'assistant'
  content: string
  correct?: boolean | null
  /** Рисунок учебника или чертёж — стоит в ленте у своей реплики. */
  image?: DialogueImage
}

/**
 * Экран задачи.
 *
 * На телефоне условие стоит сверху и сворачивается в шапку — так оно не уезжает
 * из виду вместе с прокруткой диалога (в Telegram это главная беда: условие
 * теряется на третьей реплике). На широком экране условие живёт в левой колонке
 * и видно всегда.
 */
export default function TaskScreen({ me }: { me: Me }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<{ task: Task | null }>({
    queryKey: ['task', 'current'],
    queryFn: () => apiGet('/api/tasks/current'),
    // Страницу перезагрузили посреди ответа: реплика ученика сохранена, а ответ
    // сервер дописывает в диалог сам — опрашиваем, пока он не появится.
    refetchInterval: (query) => {
      const ждёт = query.state.data?.task?.pending
      return ждёт && ждёт.seconds < ОЖИДАНИЕ_ОТВЕТА_С ? 4000 : false
    },
  })

  const task = data?.task ?? null
  const [replies, setReplies] = useState<Reply[] | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [input, setInput] = useState('')
  const [rewards, setRewards] = useState<Rewards | null>(null)
  const [error, setError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const { text: streamText, running, error: streamError, run } = useStreamRunner()

  // Ответ задерживается: 30 с без единого слова — говорим об этом, а не молчим
  // под «Думаю…». 13 октября 2026 ученик ждал так больше двух минут и решил,
  // что всё сломалось.
  const [долго, setДолго] = useState(false)
  useEffect(() => {
    if (!running || streamText) {
      setДолго(false)
      return
    }
    const таймер = window.setTimeout(() => setДолго(true), ДОЛГО_С * 1000)
    return () => window.clearTimeout(таймер)
  }, [running, streamText])

  // Неотвеченная реплика из прошлого захода на страницу (перезагрузка посреди
  // ответа). В своём заходе реплика уже стоит в ленте, а ответ идёт потоком.
  const ждёт = !running && replies === null ? (task?.pending ?? null) : null
  const ждётСвежая = ждёт !== null && ждёт.seconds < ОЖИДАНИЕ_ОТВЕТА_С

  // Первое открытие: диалог берём из состояния занятия, дальше ведём сами.
  const исходная = (task?.dialogue as Reply[] | undefined) ?? []
  const лента: Reply[] = replies ?? исходная

  // Новая реплика и «Думаю…» — в поле зрения. Без прокрутки они вставали ниже
  // края экрана, и ученик видел под последним ответом пустоту: ушла ли реплика,
  // думает ли наставник (живой прогон 10 октября 2026). Первое открытие не
  // крутим — там важнее условие.
  //
  // Готовый ответ наставника встаёт НАЧАЛОМ к верху экрана, а не концом к низу:
  // на телефоне нижняя панель занимает заметную часть экрана, и прокрутка «к
  // концу» прятала начало ответа над краем — читать приходилось снизу вверх
  // (живой прогон 22 октября 2026). Рисунок, пришедший после ответа, ленту не
  // двигает — иначе начало ответа уезжало бы снова.
  const конецЛенты = useRef<HTMLDivElement>(null)
  const последняяРеплика = useRef<HTMLDivElement>(null)
  const былоРеплик = useRef<number | null>(null)
  useEffect(() => {
    const было = былоРеплик.current
    былоРеплик.current = лента.length
    if (было === null) return
    const последняя = лента[лента.length - 1]
    if (лента.length > было && последняя?.role === 'assistant') {
      if (последняя.content.trim()) {
        последняяРеплика.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      return
    }
    if (лента.length <= было && !running) return
    конецЛенты.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    // Только по числу реплик и смене «отвечает»: на каждый кусок потока лента
    // не крутится — ученик в это время может читать выше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [лента.length, running])

  /** Дописывает реплики в ленту поверх её СВЕЖЕГО состояния: реплика ученика
   *  встаёт в ленту сразу при отправке, а ответ приходит через 20–40 секунд —
   *  к этому моменту лента в замыкании уже устарела. */
  function дописать(...новые: Reply[]) {
    setReplies((r) => [...(r ?? исходная), ...новые])
  }

  function applyDone(done: Record<string, unknown>, userText: string | null) {
    const свежая = done.task as Task | undefined
    if (свежая) queryClient.setQueryData(['task', 'current'], { task: свежая })
    setRewards((done.rewards as Rewards) ?? null)
    // Картинка ответа встаёт в ленту у своей реплики и там остаётся. Прежде
    // она висела внизу и пропадала со следующим ответом, а поиск рисунка один
    // на задачу — второй раз ученик его уже не видел. Рисунок учебника
    // приходит позже, отдельным событием (приложитьРисунок).
    const чертёж = done.figure as string | null
    const ответ: Reply = {
      role: 'assistant',
      content: typeof done.text === 'string' ? done.text : '',
      correct: (done.is_correct as boolean | null) ?? null,
    }
    if (чертёж) ответ.image = { url: чертёж, caption: 'Чертёж к задаче', kind: 'sketch' }
    дописать(...(userText ? ([{ role: 'user', content: userText }] as Reply[]) : []), ответ)
  }

  // Рисунок учебника к ответу — после него, отдельным событием потока: его
  // поиск больше не задерживает сам ответ.
  function приложитьРисунок(img: Record<string, unknown>) {
    const рисунок = img as unknown as Illustration
    дописать({
      role: 'assistant', content: '',
      image: { url: рисунок.url, caption: рисунок.caption, kind: 'textbook' },
    })
  }

  async function отправить() {
    const текст = input.trim()
    if (!текст || running || ждётСвежая) return
    setInput('')
    setError('')
    setRewards(null)
    // Реплика ученика — в ленту сразу: ответ идёт до 40 секунд, и без неё
    // ученик не видел, ушло ли его сообщение вообще.
    дописать({ role: 'user', content: текст })
    await run('/api/tasks/answer', { body: { text: текст } }, (done) => {
      if (done.kind === 'next_task') {
        следующая()
        return
      }
      applyDone(done, null)
    }, приложитьРисунок)
  }

  async function помощь() {
    setError('')
    await run('/api/tasks/help', {}, (done) => applyDone(done, null), приложитьРисунок)
  }

  async function разбор() {
    setError('')
    await run('/api/tasks/walkthrough', {}, (done) => applyDone(done, null))
  }

  // «🧠 Закрепить» — вопрос на понимание по желанию. Прежде он приходил сам,
  // в одном сообщении с «Задача решена», и ученик не знал, отвечать или идти дальше.
  async function закрепить() {
    setError('')
    await run('/api/tasks/reinforce', {}, (done) => applyDone(done, null))
  }

  async function сдать() {
    setError('')
    try {
      const res = await apiSend<{ hint: string }>('/api/tasks/submit-solution', 'POST')
      const свежая = await apiGet<{ task: Task | null }>('/api/tasks/current')
      queryClient.setQueryData(['task', 'current'], свежая)
      setReplies([...лента, { role: 'assistant', content: res.hint }])
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось перейти к сдаче решения.')
    }
  }

  async function фото(file: File) {
    setError('')
    const form = new FormData()
    form.append('file', file)
    дописать({ role: 'user', content: '📸 Фото решения' })
    await run('/api/chat/photo', { form }, (done) => applyDone(done, null))
  }

  async function следующая() {
    setError('')
    setReplies(null)
    setRewards(null)
    try {
      // Тот же способ, которым выдана эта задача (сервер помнит его в task.next):
      // тренируешь задание 5 — следующей придёт снова задание 5.
      const режим = task?.next
        ?? (task?.topic_id
          ? { mode: 'topic', node_id: task.topic_id }
          : { mode: me.mvp_mode ? 'topic' : 'study' })
      const свежая = await apiSend<Task>('/api/tasks/next', 'POST', режим)
      queryClient.setQueryData(['task', 'current'], { task: свежая })
      setCollapsed(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не получилось взять следующую задачу.')
      navigate('/')
    }
  }

  if (isLoading) return <div className="skeleton" />

  if (!task) {
    return (
      <>
        <h1>Задачи нет</h1>
        <p className="muted">Выбери тему в главном меню — и я дам задачу.</p>
        <button className="btn btn-primary btn-block btn-lg" onClick={() => navigate('/')}>
          🏠 Главная
        </button>
      </>
    )
  }

  // Кнопки под задачей — те же, что у бота (handlers/task_view.py,
  // handlers/messages.py). До решения кнопка следующей зовётся «🔀 Другая
  // задача», после — по источнику задачи (next_label). Только «📸 Фото» своя: в
  // Telegram фото шлют скрепкой, а в браузере для этого нужна кнопка.

  // После верного ответа кнопка остаётся, пока балл неполный: движок пообещал
  // ученику остальные баллы за показанный ход.
  const сдатьКнопка = (!task.solved || task.can_raise_score) &&
    task.actions.submit_solution &&
    !task.awaiting_solution && (
      <button className="btn" onClick={сдать} disabled={running}>
        📝 Сдать решение
      </button>
    )
  const помощьКнопка = !task.solved && !task.socratic_mode && (
    <button className="btn" onClick={помощь} disabled={running}>
      {task.actions.help === 'solve_together' ? '🤝 Решаем вместе' : '💡 Помощь'}
    </button>
  )
  const разборКнопка = task.solved && !task.walkthrough_shown && (
    <button className="btn" onClick={разбор} disabled={running}>
      📖 Разобрать решение
    </button>
  )
  const закрепитьКнопка = task.can_reinforce && (
    <button className="btn" onClick={закрепить} disabled={running}>
      🧠 Закрепить
    </button>
  )
  // Назад на лист теории темы: ученик приходит к задаче с листа, а вернуться
  // мог только через «Меню» и всё дерево заново (живой прогон 22 октября 2026).
  // Задача остаётся в работе — на неё ведёт главное меню.
  const теорияКнопка = task.theory_node && (
    <button
      className="btn"
      onClick={() => navigate(`/theory/${encodeURIComponent(task.theory_node!)}`)}
      disabled={running}
    >
      📖 К теории
    </button>
  )
  // До решения — «Другая задача»: сменить неподходящую, не проходя заново
  // экзамен → раздел → тему (как в боте, other_task_button).
  const следующаяКнопка = task.next_label && (
    <button
      className={task.solved ? 'btn btn-primary' : 'btn'}
      onClick={следующая}
      disabled={running}
    >
      {task.solved ? task.next_label : '🔀 Другая задача'}
    </button>
  )
  const менюКнопка = (
    <button className="btn" onClick={() => navigate('/')} disabled={running}>
      🏠 Меню
    </button>
  )
  // Журнал темы — под задачей, по тем же условиям, что у бота
  // (task_view.journal_row): нет в урезанном режиме и в сериях.
  const журналКнопка = !me.mvp_mode && task.topic_id && task.source !== 'diag' &&
    task.source !== 'exam' && (
      <button
        className="btn"
        onClick={() => navigate(`/history/${encodeURIComponent(task.topic_id!)}`)}
        disabled={running}
      >
        📜 Что я тут решал
      </button>
    )
  const фотоКнопка = (
    <button className="btn" onClick={() => fileRef.current?.click()} disabled={running}>
      📸 Фото
    </button>
  )

  return (
    <div className="task-layout">
      <section className={`card task-problem${collapsed ? ' collapsed' : ''}`}>
        {/* Шапка: тема и строкой ниже «ОГЭ · задание 3 · 1 балл» — ученик
            готовится к экзамену, и место задачи в варианте важнее пункта
            кодификатора. На телефоне по ней условие сворачивается. */}
        <button className="problem-toggle" onClick={() => setCollapsed((v) => !v)}>
          <span className="toggle-arrow" aria-hidden>{collapsed ? '▸' : '▾'}</span>
          <span className="problem-head">
            {task.topic_title ?? 'Задача'}
            {task.meta_line && <span className="small muted problem-meta">{task.meta_line}</span>}
          </span>
        </button>
        <div className="problem-body">
          <div className="scroll-x">
            <RichText text={task.problem_text} />
          </div>
          {/* Рисунок — между данными и вопросом с перечнем, как в КИМ, а не
              под пунктами «1) … 5)» (сервер режет условие, split_for_figure). */}
          {task.images.map((src) => (
            <img key={src} src={src} alt="Иллюстрация к условию" />
          ))}
          {task.problem_after_images && (
            <div className="scroll-x problem-after">
              <RichText text={task.problem_after_images} />
            </div>
          )}
          <p className="small muted" style={{ marginBottom: 0 }}>{task.hint}</p>
        </div>
      </section>

      <section>
        <div className="dialogue">
          {лента.map((r, i) => (
            <div
              key={i}
              className="msg-group"
              ref={i === лента.length - 1 ? последняяРеплика : undefined}
            >
              {r.content.trim() && (
                <div
                  className={`msg ${r.role === 'user' ? 'msg-user' : 'msg-ai'}${
                    r.correct ? ' msg-ok' : ''
                  }`}
                >
                  <RichText text={r.content} plain={r.role === 'user'} />
                </div>
              )}
              {r.image && <DialogueFigure image={r.image} />}
            </div>
          ))}

          {running && (
            <div className="msg msg-ai">
              {streamText ? (
                <>
                  <RichText text={streamText} />
                  <span className="muted"> ▌</span>
                </>
              ) : долго ? (
                <span className="muted dots">
                  Отвечаю дольше обычного — подожди ещё немного, реплика сохранена
                </span>
              ) : (
                <span className="muted dots">Думаю</span>
              )}
            </div>
          )}

          {ждёт && (
            <div className="msg-group">
              <div className="msg msg-user">
                <RichText text={ждёт.text} plain />
              </div>
              <div className="msg msg-ai">
                {ждётСвежая ? (
                  <span className="muted dots">Наставник ещё отвечает на эту реплику</span>
                ) : (
                  <span className="muted">Ответ на эту реплику не пришёл — отправь её ещё раз.</span>
                )}
              </div>
            </div>
          )}

          <div ref={конецЛенты} />

          {rewards && (
            <div className="reward">
              ⚡ +{rewards.xp} XP
              {rewards.is_daily_quest
                ? ' · 🎯 Задание дня!'
                : rewards.combo >= 3
                  ? ` · 🔥 Комбо ×${rewards.combo}!`
                  : null}
              {rewards.level_up
                ? ` · 🎉 Новый уровень: ${rewards.level_label}!`
                : rewards.to_next_level != null
                  ? ` · 📈 До уровня ${rewards.next_level_label}: ещё ${rewards.to_next_level} XP!`
                  : null}
              {rewards.badges.map((b) => ` · ${b.icon} ${b.title}`)}
            </div>
          )}

        </div>

        {(error || streamError) && <div className="error">{error || streamError}</div>}

        <div className="composer">
          {/* После решения — два ряда, как задал владелец (22 октября 2026):
              «📖 Разобрать решение · 📸 Фото» над «📝 Следующая задача · 🏠 Меню».
              Одним рядом с переносом «Фото» уезжало в третью строку отдельно от
              всех. До решения ряд прежний, один. */}
          {task.solved ? (
            <>
              <div className="row-wrap">
                {сдатьКнопка}
                {разборКнопка}
                {закрепитьКнопка}
                {фотоКнопка}
              </div>
              <div className="row-wrap">
                {следующаяКнопка}
                {менюКнопка}
                {теорияКнопка}
                {журналКнопка}
              </div>
            </>
          ) : (
            <div className="row-wrap">
              {сдатьКнопка}
              {помощьКнопка}
              {следующаяКнопка}
              {менюКнопка}
              {теорияКнопка}
              {журналКнопка}
              {фотоКнопка}
            </div>
          )}

          <div className="composer-row">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter отправляет, Shift+Enter переносит строку: развёрнутое
                // решение пишется в несколько строк, и терять их нельзя.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  отправить()
                }
              }}
              placeholder={
                task.awaiting_solution ? 'Решение целиком…' : task.solved ? 'Спроси что угодно…' : 'Твой ответ…'
              }
              rows={1}
              maxLength={1000}
            />
            <button
              className="btn btn-primary"
              onClick={отправить}
              disabled={running || ждётСвежая || !input.trim()}
            >
              ➤
            </button>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) фото(f)
            e.target.value = ''
          }}
        />
      </section>
    </div>
  )
}


/** Картинка в ленте: рисунок учебника с подписью или чертёж по просьбе. */
function DialogueFigure({ image }: { image: DialogueImage }) {
  const учебник = image.kind === 'textbook'
  return (
    <figure className="card msg-figure">
      <img
        src={image.url}
        alt={image.caption ?? (учебник ? 'Иллюстрация из учебника' : 'Чертёж к задаче')}
        loading="lazy"
      />
      <figcaption className="small muted">
        {учебник ? '📘 Иллюстрация из учебника' : '✏️ Чертёж к задаче'}
        {учебник && image.caption && (
          <div className="figure-caption">
            <RichText text={image.caption} />
          </div>
        )}
      </figcaption>
    </figure>
  )
}
